<script setup lang="ts">
import { useId } from 'vue'

/**
 * A labelled form control.
 *
 * The label is always a real `<label>` bound to the control by id, and help text is bound with
 * `aria-describedby` — passed down through the slot so the control can wire it up. Half the
 * fields in this application explain a protocol subtlety ("empty means the agent's default"), and
 * help that a screen reader never reaches is help that was not written.
 */
withDefaults(
  defineProps<{
    label: string
    help?: string
    error?: string | null
    required?: boolean
    /** Right-aligned note on the label row — a unit, a count, an "optional". */
    hint?: string
  }>(),
  { required: false },
)

const id = useId()
const describedBy = `${id}-help`
</script>

<template>
  <div class="o-field">
    <div class="label-row">
      <label :for="id">
        {{ label }}
        <span v-if="required" class="req" aria-hidden="true">*</span>
      </label>
      <span v-if="hint" class="hint">{{ hint }}</span>
    </div>

    <slot :id="id" :described-by="help || error ? describedBy : undefined" />

    <p v-if="error" :id="describedBy" class="error" role="alert">{{ error }}</p>
    <p v-else-if="help" :id="describedBy" class="help">{{ help }}</p>
  </div>
</template>

<style scoped>
.o-field {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 0;
}

.label-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-3);
}

label {
  font-size: var(--text-xs);
  font-weight: var(--weight-semibold);
  color: var(--text-strong);
}

.req {
  color: var(--critical);
  margin-left: 0.125rem;
}

.hint,
.help {
  font-size: var(--text-xs);
  color: var(--text-muted);
}

.error {
  font-size: var(--text-xs);
  color: var(--critical);
}
</style>
